import { useCallback } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";

import { analyticsResourcesValues, attributionTxt } from "../../../assets/texts/CloudAnalytics";
import { type AttributionWRef, type MetricWSnap } from "../../../types";
import { useFullScreen } from "../../../utils/dialog";
import { shortenString } from "../../../utils/string";
import { type OrganizationWSnap } from "../../IAM/Organizations/Types";
import { type AnalyticsAlertWithRef } from "../alerts";
import { type AttributionGroupWithRef } from "../attributionGroups/types";
import { type Report } from "./AttributionBrowser";

export type AnalyticsResources = {
  reports: Report[];
  metrics: MetricWSnap[];
  attributions: AttributionWRef[];
  attributionGroups: AttributionGroupWithRef[];
  alerts: AnalyticsAlertWithRef[];
  organizations: OrganizationWSnap[];
};

type Resource = {
  id: string;
  name: string;
  owner?: string;
};

export type AffectedResources = {
  id: string;
  resources: Map<string, Resource[]>;
};

const limitAttributions = 3;

type Props = {
  deleteValidationResponse: AffectedResources[] | null;
  onClose: () => void;
  resources: AnalyticsResources;
};

export const DeleteValidationDialog = ({ deleteValidationResponse, onClose, resources }: Props) => {
  const { isMobile: xsDown } = useFullScreen("sm");

  const { attributions } = resources;
  const getResource = useCallback((resource) => {
    const { resourceType, resources }: { resourceType: string; resources: Resource[] } = resource;
    if (resourceType === "dailyDigests") {
      return (
        <div>
          <b>{analyticsResourcesValues[resourceType]}</b>
        </div>
      );
    }

    const resourceNames = resources.map(
      (resource) => resource.name + (resource.owner ? ` (owner: ${resource.owner}) ` : "")
    );

    return (
      <Typography variant="body2">
        <b>{analyticsResourcesValues[resourceType]}</b>: {shortenString(resourceNames.join(", "), limitAttributions)}
      </Typography>
    );
  }, []);

  const getAttributionNameById = useCallback(
    (id: string) => attributions.find((a) => a.ref.id === id)?.data.name,
    [attributions]
  );

  const parseDeleteValidationData = useCallback(
    (data) => {
      const { id, resources }: { id: string; resources: { type: string; ids: string[] }[] } = data;
      const attributionName = getAttributionNameById(id);

      return (
        <li key={id}>
          <b>{attributionName}</b>
          <ul>
            {Object.entries(resources).map(([resourceType, blockingResources]) => (
              <li key={resourceType}>{getResource({ resourceType, resources: blockingResources })}</li>
            ))}
          </ul>
        </li>
      );
    },
    [getResource, getAttributionNameById]
  );

  const getContent = useCallback(() => {
    if (!deleteValidationResponse) {
      return null;
    }

    return (
      <Typography variant="body2">
        <ul>{deleteValidationResponse.map(parseDeleteValidationData)}</ul>
      </Typography>
    );
  }, [deleteValidationResponse, parseDeleteValidationData]);

  return (
    <Dialog fullScreen={xsDown} open={!!deleteValidationResponse} onClose={onClose} data-cy="deleteAssetDialog">
      <DialogTitle>{attributionTxt.DELETE_VALIDATION_DIALOG_TITLE}</DialogTitle>
      {!!deleteValidationResponse && (
        <DialogContent>
          <DialogContentText>
            <Typography variant="body2">{attributionTxt.DELETE_VALIDATION_DIALOG_CONTENT}</Typography>
            {getContent()}
          </DialogContentText>
        </DialogContent>
      )}
      <Divider />
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained" data-cy="cancelBtn">
          {attributionTxt.DONE}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
